import {
  MobileRow,
  MobileRowTitle,
  MobileRowValue,
  MobileTableRowWrap,
  MobileTableWrapper,
  NoResults
} from '@/components/table/mobileTable/mobileTable';
import { format } from 'date-fns';

import { IInfoIcon } from '@/assets/icons/info-icons/i-info';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import Suitability from '@/components/investors-page/investorDetailsPage/tabs/beginAllocationTab/table/suitability';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import { columns } from '../../constants';
import { ExtendedBulkAllocation } from '../../types';
import { SuitabilityWrapper } from '../cell/suitabilityDetails';

interface MobileTableProps {
  bulkAllocations: ExtendedBulkAllocation[];
  openMobileModal: (data: ExtendedBulkAllocation, modalType: string) => void;
  openInvestorDetailsPage: ({ investorId, entityId, isGroup }: { investorId: string; entityId?: string; isGroup?: boolean }) => Promise<void>;
}

const MobileTable = ({ bulkAllocations, openMobileModal, openInvestorDetailsPage }: MobileTableProps) => {
  const getPreAllocationStatus = (value: ExtendedBulkAllocation['preAllocationStatus']) => {
    if (value?.alreadyCommitted) {
      return value.committedAt ? `Committed, ${format(new Date(value.committedAt), 'LLL d, yyyy').toUpperCase()}` : 'Committed';
    } else if (value?.internalReviewRejections) {
      return value.internalReviewRejectionAt
        ? `Internal Review Rejected, ${format(new Date(value.internalReviewRejectionAt), 'LLL d, yyyy').toUpperCase()}`
        : 'Internal Review Rejected';
    } else if (value?.terminated) {
      return value.terminatedAt ? `Terminated, ${format(new Date(value.terminatedAt), 'LLL d, yyyy').toUpperCase()}` : 'Terminated';
    } else {
      return '';
    }
  };

  const TableRow = ({ el }: { el: ExtendedBulkAllocation }) => {
    return (
      <MobileTableRowWrap key={el.legalEntity.id}>
        <MobileRow>
          <MobileRowTitle>{columns[0].title}</MobileRowTitle>
          <MobileRowValue
            isRedirect
            onClick={() => {
              const { legalEntity, group } = el || {};
              const { id, family, groupId } = legalEntity || {};

              const entityIdToNavigate = !!groupId && group ? group.primaryEntity.id : id;
              openInvestorDetailsPage({ investorId: family?.id, entityId: entityIdToNavigate, isGroup: !!groupId });
            }}
          >
            {el.legalEntity?.entityName}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[1].title}</MobileRowTitle>
          <HouseholdMobileRowValue>
            <HouseholdName onClick={() => openInvestorDetailsPage({ investorId: el.legalEntity?.family.id })}>
              {el.legalEntity?.family.name}
            </HouseholdName>
            {el.legalEntity?.advisor
              ? el.legalEntity?.advisor?.initials || `${el.legalEntity?.advisor?.firstName} ${el.legalEntity?.advisor?.lastName}`
              : ''}
          </HouseholdMobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[2].title}</MobileRowTitle>
          <MobileRowValue>
            <FormattingTooltip zIndex={1000}>{+el.legalEntity?.overallCapacity}</FormattingTooltip>
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[3].title}</MobileRowTitle>
          <MobileRowValue>
            <FormattingTooltip zIndex={1000}>{+el.legalEntity?.assetClassCapacity}</FormattingTooltip>
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[4].title}</MobileRowTitle>
          <MobileRowValue onClick={() => openMobileModal(el, 'suitability')}>
            <SuitabilityWrapper>
              <Suitability suitability={el.suitability === 'Suitable' ? 'Meets Criteria' : el.suitability} width="100" />
            </SuitabilityWrapper>
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[5].title}</MobileRowTitle>
          <AmountMobileRowValue>
            {el.legalEntity.ignoreBiteSize ||
              (((el.investment.type === 'Direct' && el.committedCapital !== el.legalEntity.directBiteSize) ||
                (el.investment.type === 'Fund' && el.committedCapital !== el.legalEntity.fundBiteSize)) && (
                <Tooltip title="Differs from default bite size">
                  <IInfoIcon fill={themeVar('context.warning')} style={{ position: 'relative', top: '4px' }} />
                </Tooltip>
              ))}
            <FormattingTooltip zIndex={1000}>{+el.committedCapital}</FormattingTooltip>
          </AmountMobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[6].title}</MobileRowTitle>
          <MobileRowValue>
            {el.decisionReason === 'Show All' ? (
              <DecisionReasonWrapper>
                {el.decisionReason}
                <Tooltip
                  title="This investor is configured to see both recommended strategies and those that are not recommended. To override this default, choose one of the “No” options."
                  maxContentWidth={300}
                >
                  <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                </Tooltip>
              </DecisionReasonWrapper>
            ) : (
              el.decisionReason
            )}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>History</MobileRowTitle>
          <MobileRowValue>{getPreAllocationStatus(el.preAllocationStatus)}</MobileRowValue>
        </MobileRow>
      </MobileTableRowWrap>
    );
  };

  return (
    <MobileTableWrapper>
      {bulkAllocations.length ? (
        bulkAllocations.map((el, index) => {
          return <TableRow el={el} key={index} />;
        })
      ) : (
        <NoResults>No Results</NoResults>
      )}
    </MobileTableWrapper>
  );
};

export default MobileTable;

const HouseholdMobileRowValue = styled(MobileRowValue)`
  display: flex;
  flex-direction: column;
`;

const HouseholdName = styled.div`
  font-weight: 600;
  color: ${themeVar('font.action')};
`;

const AmountMobileRowValue = styled(MobileRowValue)`
  align-items: center;
  gap: 5px;
`;

const DecisionReasonWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 20px;
  grid-gap: 5px;
  align-items: center;
`;
